/**
 * Educational self-assessments inside Learn → Understand.
 *
 * These are reflection tools, NOT diagnostic instruments. Result copy must
 * never name a condition ("you have C-PTSD"); it describes patterns the
 * member may recognise and points to Recovery Trauma™ modules and tools.
 */

export type AssessmentQuestion = {
  /** Stable id — used for saved answers and resume, never for analytics. */
  id: string;
  text: string;
};

export type AssessmentLink = {
  label: string;
  to: string;
};

export type AssessmentBand = {
  key: "low" | "some" | "often" | "high";
  /** Inclusive lower bound of the score percentage for this band. */
  minPercent: number;
  /** Non-diagnostic headline, e.g. "You may recognise some patterns related to…" */
  headline: string;
  /** Two or three plain-English sentences of context. */
  body: string;
};

/** Broad browsing groups shown on the Self-Assessments index. */
export type AssessmentCategoryKey =
  | "nervous-system"
  | "emotions"
  | "relationships"
  | "thoughts"
  | "self-identity"
  | "daily-life";

export type AssessmentCategory = {
  key: AssessmentCategoryKey;
  emoji: string;
  label: string;
  blurb: string;
};

export const ASSESSMENT_CATEGORIES: AssessmentCategory[] = [
  {
    key: "nervous-system",
    emoji: "🌿",
    label: "Nervous System & Survival Responses",
    blurb: "How your body responds to stress, threat and overwhelm.",
  },
  {
    key: "emotions",
    emoji: "🌊",
    label: "Emotions",
    blurb: "How feelings arrive, move through you and settle again.",
  },
  {
    key: "relationships",
    emoji: "🫂",
    label: "Relationships & Attachment",
    blurb: "How closeness, distance and trust tend to feel for you.",
  },
  {
    key: "thoughts",
    emoji: "🧠",
    label: "Thoughts & Protective Patterns",
    blurb: "The mental habits that once kept you safe.",
  },
  {
    key: "self-identity",
    emoji: "🪞",
    label: "Self & Identity",
    blurb: "How you see, speak to and relate to yourself.",
  },
  {
    key: "daily-life",
    emoji: "🌙",
    label: "Daily Life",
    blurb: "How patterns show up in ordinary days and nights.",
  },
];

/**
 * Grouped follow-on navigation shown with a result. Purely educational
 * wellbeing navigation — never a treatment recommendation.
 */
export type AssessmentExplore = {
  /** 📚 Understand — Learning Hub content. */
  understand: AssessmentLink[];
  /** 🧘 Try — exercises and regulation tools. */
  try: AssessmentLink[];
  /** ✍️ Reflect — journals and reflection prompts. */
  reflect: AssessmentLink[];
};

export type Assessment = {
  slug: string;
  emoji: string;
  title: string;
  /** One line shown on the card and under the title. */
  tagline: string;
  /** What this reflects on, in plain English. Shown before starting. */
  intro: string;
  questions: AssessmentQuestion[];
  bands: AssessmentBand[];
  /** "Explore next" links shown with every result (legacy flat list). */
  next?: AssessmentLink[];
  /** Grouped Understand / Try / Reflect links shown after a result. */
  explore?: AssessmentExplore;
  /** Browsing category. Defaults to "nervous-system" when omitted. */
  category?: AssessmentCategoryKey;
  /** Extra search terms so "freeze" or "sleep" find the right assessments. */
  keywords?: string[];
  /**
   * Release phase. Phase 2 (strength-based) assessments can be added with
   * `phase: 2` and will surface automatically once marked `published`.
   */
  phase?: 1 | 2;
};

/** The four answer options, scored 0–3. */
export const ASSESSMENT_OPTIONS = [
  { value: 0, label: "Never" },
  { value: 1, label: "Sometimes" },
  { value: 2, label: "Often" },
  { value: 3, label: "Very often" },
] as const;

export const ASSESSMENT_DISCLAIMER =
  "This self-assessment is for education and self-reflection only. It cannot diagnose PTSD, C-PTSD or any other mental-health condition. If you are concerned about your symptoms or wellbeing, consider speaking with an appropriately qualified healthcare professional.";

export function bandFor(assessment: Assessment, percent: number): AssessmentBand {
  const sorted = [...assessment.bands].sort((a, b) => b.minPercent - a.minPercent);
  return sorted.find((b) => percent >= b.minPercent) ?? sorted[sorted.length - 1]!;
}
